import * as THREE from 'three'
import { Markers } from "./markers";
import { WaypointMarkerType } from '@/common/types/elements/visualizer/marker';

import { WaypointMarkerModel } from "./waypointMarker"
import { InteractionManager } from "three.interactive";
import { Group, Scene } from "three";
import { markerMaterial_hidden, markerMaterial_hover, markerMaterial_inactive, markerMaterial_selected } from "./constants";
import { type MarkerMeshType, type WaypointEventCallback } from 'Common/types/elements/visualizer/visualizerTypes'


interface WaypointMarkers {
    selectedMarkerModel: WaypointMarkerModel
    selectedMarker: WaypointMarkerType
}

class WaypointMarkers extends Markers {
    constructor(markerList: Array<WaypointMarkerType>,
        markerEventCallback: WaypointEventCallback,
        interactionManager: InteractionManager,
        root: Group | Scene,
        selectedMarker: WaypointMarkerType,
        showAllMarkers?: boolean,
        group?: any) {
        super(markerList, markerEventCallback, interactionManager, root, selectedMarker, showAllMarkers, group)
    }

    getWaypointByImage(uuid) {
        return this.markerList.find((wp: WaypointMarkerType) => wp.meta.imageUuid == uuid)
    }

    createMarker(marker: WaypointMarkerType): WaypointMarkerModel {
        const markerModel = new WaypointMarkerModel(marker, this.markerEventCallback, this.interactionManager, null, {
            hiddenMaterial: markerMaterial_hidden,
            hoverMaterial: markerMaterial_hover,
            inactiveMaterial: markerMaterial_inactive,
            selectedMaterial: markerMaterial_selected,
        })
        return markerModel
    }

    /**
     * Populates markers
     */
    populate(groupName = 'markers') {
        const existingMarkerGroup = this.root.getObjectByName(groupName)
        if (existingMarkerGroup !== undefined) {
            existingMarkerGroup.children.forEach((child) => {
                this.interactionManager.remove(child)
            })
            this.root.remove(existingMarkerGroup)
        }

        this.group = new THREE.Group()
        this.group.name = groupName
        this.group.position.set(0, 0, 0)
        this.group.rotation.set(0, 0, 0)
        this.group.scale.set(1, 1, 1)

        if (this.showAllMarkers) {
            this.populateAllMarkers()
        } else {
            this.populateSingleMarker()
        }
        this.root.add(this.group)
    }

    /**
     * populate single marker
     */
    populateSingleMarker() {
        const markerModel = this.createMarker(this.selectedMarker)
        markerModel.setMeshToSelectedMaterial()
        this.group.add(markerModel.mesh)
        this.selectedMarkerModel = markerModel
    }

    /**
     * Populates all marker based on the given marker data
     */
    populateAllMarkers() {
        this.markerList.forEach((marker: WaypointMarkerType) => {
            const markerModel = this.createMarker(marker)
            if (marker.meta.imageUuid === this.selectedMarker.meta.imageUuid) {
                markerModel.setMeshToSelectedMaterial()
                this.selectedMarkerModel = markerModel
            } else {
                markerModel.setMeshToInactiveMaterial()
            }
            this.group.add(markerModel.mesh)
        })
    }

    /**
     * Sets the value of showAllMarkers
     * @param value Boolean value to set showAllMarker to
     */
    setShowAllMarkers(value: boolean) {
        this.showAllMarkers = value
        this.populate('waypoints')
    }

    /**
     * Sets up a single waypoint marker model
     * @param marker WaypointpMarkerType Object to set selected marker to
     */
    setSingleSelectedMarker(marker: WaypointMarkerType) {
        this.selectedMarker = marker
        this.populate('waypoints')
    }

    /**
     * Sets a selected marker when all markers are being displayed
     * @param WaypointpMarkerType MarkerType Object to set selected marker to
     */
    setSelectedMarkerFromAll(marker: WaypointMarkerType) {

        const previousSelectedMarkerModel = this.selectedMarkerModel
        let selectedMarkerModel
        const existingMarker =
            this.markerList.findIndex((wp: WaypointMarkerType) => {
                return wp.meta.imageUuid === marker.meta.imageUuid
            }) !== -1
        if (existingMarker) {
            const selectedMarkerMesh = this.group.getObjectByName(marker.meta.imageUuid) as MarkerMeshType
            selectedMarkerModel = new WaypointMarkerModel(marker, null, this.interactionManager, selectedMarkerMesh, {
                hiddenMaterial: markerMaterial_hidden,
                hoverMaterial: markerMaterial_hover,
                inactiveMaterial: markerMaterial_inactive,
                selectedMaterial: markerMaterial_selected,
            })
        } else {
            selectedMarkerModel = new WaypointMarkerModel(marker, null, this.interactionManager, null, {
                hiddenMaterial: markerMaterial_hidden,
                hoverMaterial: markerMaterial_hover,
                inactiveMaterial: markerMaterial_inactive,
                selectedMaterial: markerMaterial_selected,
            })
        }


        // previousSelectedMarkerModel.mesh.dispatchEvent({ type: MarkerEventType.mouseout })
        previousSelectedMarkerModel.setMeshToInactiveMaterial()

        this.group.add(selectedMarkerModel.mesh)
        this.selectedMarkerModel = selectedMarkerModel
        this.selectedMarkerModel.setMeshToSelectedMaterial()
        this.selectedMarker = marker
    }

    /**
     * Sets the selected marker based on the given MarkerType object
     * @param marker MarkerType Object to set selected marker to
     */
    setSelectedMarker(marker: WaypointMarkerType) {
        if (this.showAllMarkers) {
            this.setSelectedMarkerFromAll(marker)
        } else {
            this.setSingleSelectedMarker(marker)
        }
    }
}

export { WaypointMarkers }